import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Building2, Users, LayoutGrid, KeyRound, ShieldCheck, LogOut, ExternalLink, Receipt,
  Menu as MenuIcon, PanelLeftClose, PanelLeftOpen, X, MonitorSmartphone, KeySquare, Inbox,
  UserCog, LifeBuoy,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { GestoreNotificationsBell } from '../../components/gestore/GestoreNotificationsBell';
import { GestoreIncomingSupportModal } from '../../components/gestore/GestoreIncomingSupportModal';
import GestoreRestaurants from './GestoreRestaurants';
import GestoreUsers from './GestoreUsers';
import GestoreSections from './GestoreSections';
import GestoreLicenses from './GestoreLicenses';
import GestoreReceipts from './GestoreReceipts';
import GestoreDevices from './GestoreDevices';
import GestoreIntegrations from './GestoreIntegrations';
import GestoreLeads from './GestoreLeads';
import GestoreManagers from './GestoreManagers';
import GestoreRemoteSupport from './GestoreRemoteSupport';
import GestoreRemoteViewer from './GestoreRemoteViewer';

function buildNavGroups(role) {
  const isRoot = (role || 'root') === 'root';
  const registry = [
    { id: 'businesses', labelKey: 'gestore.navBusinesses', icon: Building2, path: '/gestore' },
    { id: 'users', labelKey: 'gestore.navUsers', icon: Users, path: '/gestore/utenti' },
    { id: 'sections', labelKey: 'gestore.navSections', icon: LayoutGrid, path: '/gestore/sezioni' },
  ];
  if (isRoot) {
    registry.push({ id: 'managers', labelKey: 'gestore.navManagers', icon: UserCog, path: '/gestore/gestori' });
  }
  const ops = [
    { id: 'remoteSupport', labelKey: 'gestore.navRemoteSupport', icon: LifeBuoy, path: '/gestore/assistenza-remota' },
    { id: 'leads', labelKey: 'gestore.navLeads', icon: Inbox, path: '/gestore/richieste' },
    { id: 'receipts', labelKey: 'gestore.navReceipts', icon: Receipt, path: '/gestore/scontrini' },
    { id: 'licenses', labelKey: 'gestore.navLicenses', icon: KeyRound, path: '/gestore/licenze' },
  ];
  return [
    { labelKey: 'gestore.navGroupRegistry', items: registry },
    { labelKey: 'gestore.navGroupOps', items: ops },
    {
      labelKey: 'gestore.navGroupSystem',
      items: [
        { id: 'devices', labelKey: 'gestore.navDevices', icon: MonitorSmartphone, path: '/gestore/dispositivi' },
        { id: 'integrations', labelKey: 'gestore.navIntegrations', icon: KeySquare, path: '/gestore/chiavi-api' },
      ],
    },
  ];
}

const SIDEBAR_KEY = 'gestoreSidebarCollapsed';

function SidebarNav({ groups, collapsed, isActive, onNavigate, onGoToApp, onLogout, t, showBell = false }) {
  return (
    <div className="h-dvh flex flex-col bg-foreground text-background">
      {/* Brand */}
      <div className={cn('h-16 flex items-center border-b border-background/10', collapsed ? 'justify-center px-2' : 'gap-2 px-5')}>
        <div className="h-9 w-9 rounded-xl bg-background/15 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-5 w-5" />
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="font-display font-semibold text-sm truncate">{t('gestore.panel')}</div>
            <div className="text-xs text-background/60 truncate">{t('common.appName')}</div>
          </div>
        )}
        {!collapsed && showBell && <GestoreNotificationsBell tone="dark" />}
      </div>

      {/* Nav */}
      <ScrollArea className="flex-1">
        <nav className="p-3 space-y-4">
          {groups.map((group) => (
            <div key={group.labelKey} className="space-y-1">
              {!collapsed && (
                <div className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-background/40">
                  {t(group.labelKey)}
                </div>
              )}
              {group.items.map((n) => {
                const Icon = n.icon;
                const active = isActive(n.path);
                const btn = (
                  <button
                    key={n.path}
                    data-testid={`gestore-sidebar-nav-${n.id}`}
                    onClick={() => onNavigate(n.path)}
                    className={cn(
                      'w-full h-11 rounded-lg flex items-center text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      collapsed ? 'justify-center px-0' : 'gap-3 px-3',
                      active ? 'bg-background/15 text-background' : 'text-background/70 hover:bg-background/10 hover:text-background'
                    )}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {!collapsed && <span className="truncate">{t(n.labelKey)}</span>}
                  </button>
                );
                if (collapsed) {
                  return (
                    <Tooltip key={n.path} delayDuration={0}>
                      <TooltipTrigger asChild>{btn}</TooltipTrigger>
                      <TooltipContent side="right">{t(n.labelKey)}</TooltipContent>
                    </Tooltip>
                  );
                }
                return btn;
              })}
            </div>
          ))}
        </nav>
      </ScrollArea>

      {/* Footer actions */}
      <div className="p-3 border-t border-background/10 space-y-1">
        {[
          { id: 'goToApp', icon: ExternalLink, label: t('gestore.goToApp'), onClick: onGoToApp, testid: 'gestore-go-to-app-button' },
          { id: 'logout', icon: LogOut, label: t('gestore.logout'), onClick: onLogout, testid: 'gestore-logout-button' },
        ].map((a) => {
          const Icon = a.icon;
          const btn = (
            <button
              key={a.id}
              onClick={a.onClick}
              data-testid={a.testid}
              className={cn(
                'w-full h-10 rounded-lg flex items-center text-sm text-background/70 hover:bg-background/10 hover:text-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                collapsed ? 'justify-center px-0' : 'gap-3 px-3'
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">{a.label}</span>}
            </button>
          );
          if (collapsed) {
            return (
              <Tooltip key={a.id} delayDuration={0}>
                <TooltipTrigger asChild>{btn}</TooltipTrigger>
                <TooltipContent side="right">{a.label}</TooltipContent>
              </Tooltip>
            );
          }
          return btn;
        })}
      </div>
    </div>
  );
}

export default function GestoreApp() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('rmp_admin_token');
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_KEY) === 'true');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [role, setRole] = useState('root');

  useEffect(() => {
    if (!token) return;
    import('../../lib/adminApi').then(({ default: adminApi }) => {
      adminApi.get('/admin/me').then(({ data }) => setRole(data.role || 'root')).catch(() => {});
    });
  }, [token]);

  if (!token) return <Navigate to="/gestore/login" replace />;

  const navGroups = buildNavGroups(role);
  const logout = () => { localStorage.removeItem('rmp_admin_token'); navigate('/gestore/login', { replace: true }); };
  const goToApp = () => navigate('/login');
  const isActive = (p) => (p === '/gestore' ? location.pathname === '/gestore' : location.pathname.startsWith(p));
  const toggleCollapsed = () => {
    setCollapsed((c) => { const next = !c; localStorage.setItem(SIDEBAR_KEY, String(next)); return next; });
  };
  const mobileNavigate = (p) => { navigate(p); setMobileOpen(false); };

  // Current page title for mobile topbar
  const currentItem = navGroups.flatMap((g) => g.items).find((n) => isActive(n.path));
  const currentTitle = currentItem ? t(currentItem.labelKey) : t('gestore.panel');

  return (
    <TooltipProvider>
      <div className="min-h-dvh bg-background">
        {/* Desktop sidebar (fixed) */}
        <aside
          className={cn(
            'hidden md:flex md:flex-col md:fixed md:inset-y-0 md:left-0 z-30 transition-[width] duration-200',
            collapsed ? 'md:w-[76px]' : 'md:w-[260px]'
          )}
        >
          <SidebarNav
            groups={navGroups}
            collapsed={collapsed}
            isActive={isActive}
            onNavigate={navigate}
            onGoToApp={goToApp}
            onLogout={logout}
            t={t}
            showBell
          />
          {/* Collapse toggle */}
          <button
            data-testid="gestore-sidebar-collapse-toggle"
            onClick={toggleCollapsed}
            aria-label={collapsed ? t('gestore.expandSidebar') : t('gestore.collapseSidebar')}
            className="absolute -right-3 top-20 h-7 w-7 rounded-full bg-card border border-border text-foreground shadow-sm-token flex items-center justify-center hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </aside>

        {/* Mobile topbar */}
        <header
          data-testid="gestore-mobile-topbar"
          className="md:hidden sticky top-0 z-40 bg-background/90 backdrop-blur border-b border-border"
        >
          <div className="h-14 px-4 flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              data-testid="gestore-mobile-nav-open-button"
              aria-label={t('gestore.openNav')}
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-lg bg-foreground text-background flex items-center justify-center shrink-0">
                <ShieldCheck className="h-4 w-4" />
              </div>
              <span className="font-display font-semibold truncate">{currentTitle}</span>
            </div>
            <div className="ml-auto">
              <GestoreNotificationsBell tone="light" idSuffix="-mobile" />
            </div>
          </div>
        </header>

        {/* Mobile drawer */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent
            side="left"
            hideClose
            data-testid="gestore-mobile-nav-drawer"
            className="w-[86vw] max-w-[320px] p-0 bg-foreground text-background border-r-0"
          >
            <SheetTitle className="sr-only">{t('gestore.panel')}</SheetTitle>
            <SheetDescription className="sr-only">{t('gestore.openNav')}</SheetDescription>
            <button
              onClick={() => setMobileOpen(false)}
              aria-label={t('gestore.rCancel')}
              className="absolute right-3 top-3 z-10 h-9 w-9 rounded-md flex items-center justify-center text-background/70 hover:bg-background/10 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarNav
              groups={navGroups}
              collapsed={false}
              isActive={isActive}
              onNavigate={mobileNavigate}
              onGoToApp={() => { goToApp(); setMobileOpen(false); }}
              onLogout={logout}
              t={t}
            />
          </SheetContent>
        </Sheet>

        {/* Main content */}
        <main className={cn('min-h-dvh transition-[padding] duration-200', collapsed ? 'md:pl-[76px]' : 'md:pl-[260px]')}>
          <Routes>
            <Route index element={<GestoreRestaurants />} />
            <Route path="utenti" element={<GestoreUsers />} />
            <Route path="sezioni" element={<GestoreSections />} />
            <Route path="richieste" element={<GestoreLeads />} />
            <Route path="scontrini" element={<GestoreReceipts />} />
            <Route path="licenze" element={<GestoreLicenses />} />
            <Route path="dispositivi" element={<GestoreDevices />} />
            <Route path="chiavi-api" element={<GestoreIntegrations />} />
            <Route path="assistenza-remota" element={<GestoreRemoteSupport />} />
            <Route path="assistenza-remota/sessione/:sid" element={<GestoreRemoteViewer />} />
            <Route path="gestori" element={role === 'root' ? <GestoreManagers /> : <Navigate to="/gestore" replace />} />
            <Route path="*" element={<Navigate to="/gestore" replace />} />
          </Routes>
        </main>

        {/* Pop-up globale: richieste di assistenza in arrivo dai locali */}
        <GestoreIncomingSupportModal />
      </div>
    </TooltipProvider>
  );
}
